import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Inbox, BookOpen, LogOut, Trash2, Loader2, KeyRound } from "lucide-react";
import { api, getToken, clearToken } from "@/admin/api";
import { CaseStudyManager } from "./CaseStudyManager";
import { PasswordChange } from "./PasswordChange";

export default function AdminDashboard() {
  const [tab, setTab] = useState("inquiries");
  const [inquiries, setInquiries] = useState([]);
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [inq, cs] = await Promise.all([
        api("/api/admin/inquiries"),
        api("/api/admin/case-studies"),
      ]);
      setInquiries(inq);
      setStudies(cs);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    clearToken();
    navigate("/admin/login");
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await api(`/api/admin/inquiries/${id}`, { method: "DELETE" });
      setInquiries(inquiries.filter((i) => i.id !== id));
      toast.success("Inquiry deleted.");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const reloadStudies = async () => {
    setStudies(await api("/api/admin/case-studies"));
  };

  return (
    <div className="min-h-screen bg-[#FAFAFC]">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#0FA89E] font-semibold">
              May Forward Tech CMS
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors">
              View site
            </a>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:border-red-300 hover:text-red-600 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-dashboard">
        <div className="flex gap-3 mb-8">
          <button
            data-testid="admin-tab-inquiries"
            onClick={() => setTab("inquiries")}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
              tab === "inquiries"
                ? "bg-[#0FA89E] text-white"
                : "bg-white border border-slate-200 text-slate-600 hover:border-[#0FA89E]"
            }`}
          >
            <Inbox className="w-4 h-4" />
            Inquiries ({inquiries.length})
          </button>
          <button
            data-testid="admin-tab-case-studies"
            onClick={() => setTab("case-studies")}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
              tab === "case-studies"
                ? "bg-[#0FA89E] text-white"
                : "bg-white border border-slate-200 text-slate-600 hover:border-[#0FA89E]"
            }`}
          >
            <BookOpen className="w-4 h-4" />
            Case studies ({studies.length})
          </button>
          <button
            data-testid="admin-tab-settings"
            onClick={() => setTab("settings")}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
              tab === "settings"
                ? "bg-[#0FA89E] text-white"
                : "bg-white border border-slate-200 text-slate-600 hover:border-[#0FA89E]"
            }`}
          >
            <KeyRound className="w-4 h-4" />
            Settings
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#0FA89E]" />
          </div>
        ) : tab === "inquiries" ? (
          <div data-testid="admin-inquiries" className="space-y-4">
            <h2 className="font-display text-xl font-semibold text-[#0F172A] mb-6">
              Contact inquiries ({inquiries.length})
            </h2>
            {inquiries.map((i) => (
              <div
                key={i.id}
                data-testid={`inquiry-row-${i.id}`}
                className="rounded-xl border border-slate-200 bg-white p-6"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-1">
                      <h3 className="font-semibold text-[#0F172A]">{i.name}</h3>
                      <span className="rounded-full bg-teal-50 text-teal-700 border border-teal-200 px-2.5 py-0.5 text-xs font-semibold">
                        {i.service}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 mb-3">
                      {i.email}
                      {i.company ? ` · ${i.company}` : ""} ·{" "}
                      {new Date(i.created_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </p>
                    <p className="text-base text-slate-600 leading-relaxed">{i.message}</p>
                  </div>
                  <button
                    data-testid={`inquiry-delete-${i.id}`}
                    onClick={() => deleteInquiry(i.id)}
                    className="shrink-0 p-2.5 rounded-lg border border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-600 transition-colors"
                    aria-label="Delete inquiry"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {inquiries.length === 0 && (
              <p className="text-slate-500 text-sm py-12 text-center">
                No inquiries yet — they appear here when someone submits the contact form.
              </p>
            )}
          </div>
        ) : tab === "case-studies" ? (
          <CaseStudyManager studies={studies} reload={reloadStudies} />
        ) : (
          <PasswordChange />
        )}
      </div>
    </div>
  );
}
